import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Standardized page hero — one title, an optional description, and an
 * optional right-aligned actions slot. Every route above the fold uses this
 * so the "text-2xl font-semibold + max-w-[65ch] description" pattern doesn't
 * drift by a font weight or a spacing token per page.
 *
 * DESIGN.md notes:
 *   - Title: `text-2xl font-semibold tracking-tight` (page titles are the
 *     only place `tracking-tight` earns its keep — headings above `text-2xl`
 *     would be shouting on an admin surface).
 *   - Description: `max-w-[65ch] text-sm text-muted-foreground`.
 *   - Never use a tiny-tracked uppercase eyebrow on top of every page — that
 *     was the 2023-era SaaS scaffold and is one of impeccable's shared bans.
 *     `scopeLabel` on the Shell already renders a single scoped eyebrow at
 *     the top of the app frame; page-level eyebrows are almost never needed.
 */
interface PageHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Right-aligned slot for actions (buttons, filters) or a hero metric. */
  actions?: React.ReactNode;
  /** Optional slot rendered under the header block, above the page's main
   *  content. Use for banners, cron drawers, sub-nav, etc. */
  children?: React.ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  actions,
  children,
  className,
}: PageHeaderProps): JSX.Element {
  return (
    <header className={cn('space-y-3', className)}>
      <div
        className={cn(
          'flex flex-wrap items-start justify-between',
          actions ? 'gap-6' : 'gap-3',
        )}
      >
        <div className="min-w-0">
          <h1 className="text-[1.625rem] font-bold leading-tight tracking-[-0.03em]">{title}</h1>
          {description ? (
            <p className="mt-1 max-w-[65ch] text-sm text-muted-foreground">
              {description}
            </p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {children}
    </header>
  );
}
